// Translation dictionaries for TrustHer Home
// Keys are grouped by page/component. Use dot-notation with useTranslation().

const en = {
    common: {
        joinHomeownerCommunity: "Join the Homeowner Community",
        applyFoundingProvider: "Apply as a Founding Provider",
        becomeFoundingProvider: "Become a Founding Provider",
        joinTheCommunity: "Join the Community",
        learnMoreForHomeowners: "Learn more for homeowners",
        readOurStory: "Read our story",
        homeownerCommunityShort: "Homeowner Community",
        providerApplication: "Provider application",
    },
    header: {
        nav: {
            home: "Home",
            about: "About",
            forHomeowners: "For Homeowners",
            forProviders: "For Service Providers",
            contact: "Contact",
        },
        cta: "Join the Community",
        languageLabel: "Language",
    },
    footer: {
        tagline:
            "A trusted directory connecting women homeowners with women-owned home service providers, contractors, and trades professionals.",
        exploreHeading: "Explore",
        followHeading: "Follow our launch journey",
        followBody: "Be the first to hear when the directory goes live in your area.",
        rightsReserved: "All rights reserved.",
        links: {
            home: "Home",
            about: "About Us",
            homeowners: "For Homeowners",
            providers: "For Service Providers",
            contact: "Contact",
        },
    },
    home: {
        seo: {
            title: "The Home Services Directory Built for Women, by Women",
            description:
                "TrustHer Home is a trusted directory connecting women homeowners with women-owned home service providers, contractors, and trades professionals. Join the community.",
        },
        hero: {
            eyebrow: "Coming Soon - Pre-launch",
            title1: "The Home Services Directory",
            titleAccent1: "Built for Women,",
            titleAccent2: "by Women.",
            body:
                "We're creating a trusted directory that connects women homeowners with women-owned home service providers, contractors, and trades professionals.",
            ctaHomeowner: "Join the Homeowner Community",
            ctaProvider: "Apply as a Founding Provider",
            trustText:
                "Trusted by women homeowners & providers joining every week.",
            badgeEyebrow: "Trusted",
            badgeText: "Women-Owned Professionals",
            missionEyebrow: "Mission",
            missionText: "Built for trust. Designed for women.",
        },
        who: {
            eyebrow: "Who we are",
            title1: "A community-first directory",
            titleAccent: "with a mission.",
            body1:
                "TrustHer Home is a forthcoming directory dedicated to making it easier for women homeowners to discover, hire, and support women-owned home service providers in their communities.",
            body2:
                "We're building the directory we wished we had - one where recommendations feel personal, professionals feel respected, and every home project starts with confidence.",
            cta: "Read our story",
        },
        why: {
            eyebrow: "Why this directory matters",
            title1: "Two underserved sides of the home services industry -",
            titleAccent: "finally connected.",
            homeownersHeading: "For homeowners",
            homeownersBody:
                "Women homeowners want trusted, respectful, professional service - with clear pricing and clear communication. They deserve a vetted place to start.",
            providersHeading: "For women-owned businesses",
            providersBody:
                "Skilled women in the trades and home services often go undiscovered. We're putting them at the center, where they belong.",
        },
        how: {
            eyebrow: "How it will work",
            title1: "A simple, thoughtful",
            titleAccent: "experience.",
            body:
                "Once we launch, finding the right women-owned professional for your home will be intuitive - by category, by location, by trust.",
            step1Title: "Search by category and location",
            step1Body:
                "Plumbing, electrical, interior design, landscaping and more - filtered by your area.",
            step2Title: "Browse trusted women-owned businesses",
            step2Body:
                "View clear profiles, services, and how to get in touch with each professional.",
            step3Title: "Hire with confidence",
            step3Body:
                "Reach out directly and support women-owned businesses in your community.",
        },
        benefits: {
            homeownersEyebrow: "For Homeowners",
            homeownersTitle1: "Hire with trust.",
            homeownersTitleAccent: "Support women.",
            homeownersItems: [
                "Easily find women-owned providers in your area",
                "Search by service category and location",
                "Discover trusted professionals for any home project",
                "Be the first to know when we launch near you",
            ],
            homeownersCta: "Learn more for homeowners",
            providersEyebrow: "For Service Providers",
            providersTitle1: "Be discovered.",
            providersTitleAccent: "Be celebrated.",
            providersItems: [
                "Get found by homeowners actively seeking women-owned businesses",
                "Join as a founding provider and shape the future of the directory",
                "Receive early access and exclusive launch-day visibility",
                "Future support for profiles, categories, locations, and contact info",
            ],
            providersCta: "Apply as a Founding Provider",
        },
        signup: {
            eyebrow: "Be the first to know",
            title1: "Join the homeowner",
            titleAccent: "community.",
            body:
                "Be notified the moment the directory launches in your area. No spam - just one personal note when we go live.",
            cta: "Join the Homeowner Community",
        },
        comingSoon: {
            eyebrow: "Launching soon",
            title1: "We're launching soon.",
            titleAccent: "Be one of the first.",
            body:
                "Whether you're a homeowner looking for trusted help or a women-owned business ready to be discovered - your spot is waiting.",
            ctaHomeowner: "Homeowner Community",
            ctaProvider: "Provider application",
        },
    },
    homeowners: {
        seo: {
            title: "For Homeowners - Find Trusted Women-Owned Home Services",
            description:
                "A directory for women homeowners to find trusted, women-owned home service providers, contractors, and trades professionals. Join the homeowner community.",
        },
        hero: {
            eyebrow: "For Homeowners",
            title1: "Find women-owned home services",
            titleAccent: "you can trust.",
            body:
                "From plumbing and electrical to interior design and landscaping - soon you'll be able to search, find, and hire trusted women-owned providers in your area, all in one place.",
            cta: "Join the Community",
        },
        expect: {
            eyebrow: "What to expect",
            title1: "Designed around",
            titleAccent: "how you actually hire.",
            cards: [
                {
                    title: "Search by service & location",
                    body: "Find the right professional by category and the area you live in.",
                },
                {
                    title: "Vetted women-owned providers",
                    body: "Every business listed will be vetted, so you can hire with confidence.",
                },
                {
                    title: "Discover & support",
                    body: "Hire women-owned businesses and support women in your community.",
                },
                {
                    title: "Local-first",
                    body: "We're launching by region - be the first to know when your area opens.",
                },
            ],
        },
        form: {
            eyebrow: "Join the homeowner community",
            title1: "Be notified when we",
            titleAccent: "launch in your area.",
            body:
                "One quick form. No spam - just a personal note from us when the directory opens near you.",
            formTitle: "Join the Homeowner Community",
        },
    },
    providers: {
        seo: {
            title: "For Service Providers - Apply to Be a Founding Provider",
            description:
                "Are you a women-owned home service provider, contractor, or trades professional? Apply to join TrustHer Home as a founding provider before launch.",
        },
        hero: {
            eyebrow: "For Women-Owned Providers",
            title1: "Be discovered by the homeowners who",
            titleAccent: "want to hire you.",
            body:
                "TrustHer Home will help women-owned contractors, tradeswomen, and home service providers get found by the homeowners actively looking for them. Apply now to join as a founding provider.",
            cta: "Apply as a Founding Provider",
        },
        why: {
            eyebrow: "Why join as a founding provider",
            title1: "Built to help your business",
            titleAccent: "stand out.",
            cards: [
                {
                    title: "Be discovered",
                    body: "Get in front of women homeowners actively looking for women-owned providers.",
                },
                {
                    title: "Founding visibility",
                    body: "Founding providers receive premium placement and recognition at launch.",
                },
                {
                    title: "Early access",
                    body: "Be the first to claim your profile, category, and service area.",
                },
                {
                    title: "Shape the directory",
                    body: "Your feedback as a founding provider will help shape the platform.",
                },
            ],
        },
        form: {
            eyebrow: "Apply now",
            title1: "Tell us about your",
            titleAccent: "business.",
            body:
                "Share a few details below. Founding providers will receive launch updates and a personal invitation to claim their profile when we go live.",
            nextTitle: "What's next",
            nextSteps: [
                "1. Submit your founding provider details",
                "2. We'll review and confirm via email",
                "3. You'll get early access at launch",
            ],
            formTitle: "Apply as a Founding Provider",
        },
    },
    about: {
        seo: {
            title: "About Us",
            description:
                "Our mission at TrustHer Home: connect women homeowners with women-owned home service providers, contractors, and trades professionals - building trust and visibility.",
        },
        hero: {
            eyebrow: "About TrustHer Home",
            title1: "We believe homeowners should feel",
            titleAccent: "confident, respected, and understood",
            title2: "when hiring help for their homes.",
        },
        story: {
            eyebrow: "Our story",
            lead:
                "TrustHer Home was born out of a simple realization: trusted, women-owned home service providers are often the hardest to find - and the most wanted.",
            p1:
                "We talked to homeowners who described the awkwardness of being talked down to, overcharged, or simply not heard. We talked to women in the trades - talented carpenters, electricians, designers, landscapers, painters - who said they rely almost entirely on word-of-mouth to grow their businesses.",
            emphasis: "Both groups deserve better.",
            p2:
                " So we're building the directory that finally puts them in front of each other - with trust, professionalism, and visibility at the core.",
        },
        values: {
            eyebrow: "Our values",
            title: "What we stand for.",
            cards: [
                {
                    title: "Trust first",
                    body: "Every provider listed will be vetted and verified. We won't shortcut on the things that matter.",
                },
                {
                    title: "Easy to navigate",
                    body: "Search by category, location, and need. No clutter - just confident decisions.",
                },
                {
                    title: "Celebrating women",
                    body: "From contractors to interior designers, we celebrate the women shaping the future of home services.",
                },
            ],
        },
        ahead: {
            eyebrow: "Looking ahead",
            title1: "We're just",
            titleAccent: "getting started.",
            p1:
                "Today we're growing our pre-launch community of homeowners and founding providers. Tomorrow, TrustHer Home will be a full searchable directory - with provider profiles, service categories, location filters, reviews, and featured listings.",
            p2:
                "If our mission resonates with you, we'd love to have you with us from the very beginning.",
            ctaHomeowner: "Join the Community",
            ctaProvider: "Become a Founding Provider",
        },
    },
    contact: {
        seo: {
            title: "Contact Us",
            description:
                "Get in touch with TrustHer Home. Reach out about the directory, partnerships, press, or to join the community.",
        },
        hero: {
            eyebrow: "Get in touch",
            title1: "We'd love to",
            titleAccent: "hear from you.",
            body:
                "We're launching soon. Join the list, ask a question, or just say hi - we read every message.",
        },
        info: {
            emailLabel: "Email",
            followLabel: "Follow our launch journey",
            launchingTitle: "We're launching soon.",
            launchingBody:
                "Join the list and be the first to know when the directory opens in your area.",
        },
        form: {
            nameLabel: "Your name",
            namePlaceholder: "Jane Doe",
            emailLabel: "Email address",
            emailPlaceholder: "jane@email.com",
            messageLabel: "Message",
            messagePlaceholder: "What's on your mind?",
            submit: "Send Message",
            submitting: "Sending...",
            successToast: "Message sent. We'll get back to you soon.",
            errorToast: "Couldn't send. Please try again.",
        },
    },
    waitlist: {
        seo: {
            title: "Join the Community",
            description:
                "Join the TrustHer Home community. Homeowners get notified at launch; women-owned providers can apply to be founding providers.",
        },
        hero: {
            eyebrow: "Get involved",
            title1: "We're launching soon.",
            titleAccent: "Be the first to know.",
            body:
                "Choose your path below - whether you're a homeowner ready to find trusted help, or a women-owned business ready to be discovered.",
        },
        tabs: {
            homeowner: "I'm a Homeowner",
            provider: "I'm a Service Provider",
        },
        homeowner: {
            title: "Join the Homeowner Community",
            body:
                "Get notified the moment the directory launches in your area. No spam - just a personal note from us at launch.",
        },
        provider: {
            title: "Founding Provider Application",
            body:
                "Founding providers receive early access, launch-day visibility, and a direct line to our team as we build the directory.",
        },
    },
    notFound: {
        seo: {
            title: "Page Not Found",
            description: "The page you're looking for doesn't exist.",
        },
        heading: "We can't find that page.",
        body:
            "The page you're looking for has moved or doesn't exist. Let's get you back home.",
        cta: "Take me home",
    },
};

const es = {
    common: {
        joinHomeownerCommunity: "Únete a la Comunidad de Propietarias",
        applyFoundingProvider: "Postúlate como Proveedora Fundadora",
        becomeFoundingProvider: "Conviértete en Proveedora Fundadora",
        joinTheCommunity: "Únete a la Comunidad",
        learnMoreForHomeowners: "Más info para propietarias",
        readOurStory: "Lee nuestra historia",
        homeownerCommunityShort: "Comunidad de Propietarias",
        providerApplication: "Postulación de proveedora",
    },
    header: {
        nav: {
            home: "Inicio",
            about: "Nosotras",
            forHomeowners: "Para Propietarias",
            forProviders: "Para Proveedoras",
            contact: "Contacto",
        },
        cta: "Únete a la Comunidad",
        languageLabel: "Idioma",
    },
    footer: {
        tagline:
            "Un directorio confiable que conecta a mujeres propietarias con proveedoras, contratistas y profesionales de oficios de servicios para el hogar propiedad de mujeres.",
        exploreHeading: "Explora",
        followHeading: "Sigue nuestro camino al lanzamiento",
        followBody:
            "Sé la primera en enterarte cuando el directorio esté disponible en tu área.",
        rightsReserved: "Todos los derechos reservados.",
        links: {
            home: "Inicio",
            about: "Sobre Nosotras",
            homeowners: "Para Propietarias",
            providers: "Para Proveedoras",
            contact: "Contacto",
        },
    },
    home: {
        seo: {
            title:
                "El Directorio de Servicios para el Hogar Hecho para Mujeres, por Mujeres",
            description:
                "TrustHer Home es un directorio confiable que conecta a mujeres propietarias con proveedoras, contratistas y profesionales de oficios propiedad de mujeres. Únete a la comunidad.",
        },
        hero: {
            eyebrow: "Muy Pronto - Prelanzamiento",
            title1: "El Directorio de Servicios para el Hogar",
            titleAccent1: "Hecho para Mujeres,",
            titleAccent2: "por Mujeres.",
            body:
                "Estamos creando un directorio confiable que conecta a mujeres propietarias con proveedoras, contratistas y profesionales de oficios de servicios para el hogar propiedad de mujeres.",
            ctaHomeowner: "Únete a la Comunidad de Propietarias",
            ctaProvider: "Postúlate como Proveedora Fundadora",
            trustText:
                "Propietarias y proveedoras se suman a nuestra comunidad cada semana.",
            badgeEyebrow: "De confianza",
            badgeText: "Profesionales propiedad de mujeres",
            missionEyebrow: "Misión",
            missionText: "Creado con confianza. Diseñado para mujeres.",
        },
        who: {
            eyebrow: "Quiénes somos",
            title1: "Un directorio centrado en la comunidad",
            titleAccent: "con una misión.",
            body1:
                "TrustHer Home es un próximo directorio dedicado a facilitar que las mujeres propietarias descubran, contraten y apoyen a proveedoras de servicios para el hogar propiedad de mujeres en sus comunidades.",
            body2:
                "Estamos construyendo el directorio que siempre quisimos tener - donde las recomendaciones se sientan personales, las profesionales se sientan respetadas y cada proyecto del hogar empiece con confianza.",
            cta: "Lee nuestra historia",
        },
        why: {
            eyebrow: "Por qué importa este directorio",
            title1:
                "Dos lados desatendidos de la industria de servicios para el hogar -",
            titleAccent: "finalmente conectados.",
            homeownersHeading: "Para propietarias",
            homeownersBody:
                "Las mujeres propietarias quieren un servicio confiable, respetuoso y profesional - con precios claros y comunicación clara. Merecen un lugar seguro donde empezar.",
            providersHeading: "Para negocios propiedad de mujeres",
            providersBody:
                "Las mujeres talentosas en los oficios y los servicios para el hogar a menudo pasan desapercibidas. Las ponemos en el centro, donde pertenecen.",
        },
        how: {
            eyebrow: "Cómo funcionará",
            title1: "Una experiencia sencilla y",
            titleAccent: "pensada con cuidado.",
            body:
                "Cuando lancemos, encontrar a la profesional correcta para tu hogar será intuitivo - por categoría, por ubicación, por confianza.",
            step1Title: "Busca por categoría y ubicación",
            step1Body:
                "Plomería, electricidad, diseño de interiores, jardinería y más - filtrado por tu área.",
            step2Title: "Explora negocios confiables propiedad de mujeres",
            step2Body:
                "Consulta perfiles claros, servicios y cómo contactar a cada profesional.",
            step3Title: "Contrata con confianza",
            step3Body:
                "Comunícate directamente y apoya a negocios propiedad de mujeres en tu comunidad.",
        },
        benefits: {
            homeownersEyebrow: "Para Propietarias",
            homeownersTitle1: "Contrata con confianza.",
            homeownersTitleAccent: "Apoya a las mujeres.",
            homeownersItems: [
                "Encuentra fácilmente proveedoras propiedad de mujeres en tu área",
                "Busca por categoría de servicio y ubicación",
                "Descubre profesionales de confianza para cualquier proyecto",
                "Sé la primera en enterarte cuando lancemos cerca de ti",
            ],
            homeownersCta: "Más info para propietarias",
            providersEyebrow: "Para Proveedoras",
            providersTitle1: "Sé descubierta.",
            providersTitleAccent: "Sé celebrada.",
            providersItems: [
                "Sé encontrada por propietarias que buscan activamente negocios propiedad de mujeres",
                "Únete como proveedora fundadora y ayuda a moldear el futuro del directorio",
                "Recibe acceso anticipado y visibilidad exclusiva el día del lanzamiento",
                "Soporte futuro para perfiles, categorías, ubicaciones y contacto",
            ],
            providersCta: "Postúlate como Proveedora Fundadora",
        },
        signup: {
            eyebrow: "Sé la primera en saber",
            title1: "Únete a la comunidad de",
            titleAccent: "propietarias.",
            body:
                "Recibe una notificación en el momento en que el directorio se lance en tu área. Sin spam - solo una nota personal cuando estemos en vivo.",
            cta: "Únete a la Comunidad de Propietarias",
        },
        comingSoon: {
            eyebrow: "Próximo lanzamiento",
            title1: "Lanzamos muy pronto.",
            titleAccent: "Sé una de las primeras.",
            body:
                "Ya sea que seas una propietaria buscando ayuda confiable o un negocio propiedad de mujeres listo para ser descubierto - tu lugar te espera.",
            ctaHomeowner: "Comunidad de Propietarias",
            ctaProvider: "Postulación de proveedora",
        },
    },
    homeowners: {
        seo: {
            title:
                "Para Propietarias - Encuentra Servicios para el Hogar Propiedad de Mujeres",
            description:
                "Un directorio para mujeres propietarias que buscan proveedoras, contratistas y profesionales de oficios propiedad de mujeres. Únete a la comunidad de propietarias.",
        },
        hero: {
            eyebrow: "Para Propietarias",
            title1: "Encuentra servicios para el hogar propiedad de mujeres",
            titleAccent: "en las que puedes confiar.",
            body:
                "Desde plomería y electricidad hasta diseño de interiores y jardinería - pronto podrás buscar, encontrar y contratar proveedoras propiedad de mujeres de confianza en tu área, todo en un solo lugar.",
            cta: "Únete a la Comunidad",
        },
        expect: {
            eyebrow: "Qué esperar",
            title1: "Diseñado en torno a",
            titleAccent: "cómo realmente contratas.",
            cards: [
                {
                    title: "Busca por servicio y ubicación",
                    body: "Encuentra a la profesional correcta por categoría y por el área donde vives.",
                },
                {
                    title: "Proveedoras verificadas",
                    body: "Cada negocio listado será verificado, para que contrates con confianza.",
                },
                {
                    title: "Descubre y apoya",
                    body: "Contrata negocios propiedad de mujeres y apoya a mujeres en tu comunidad.",
                },
                {
                    title: "Enfocado en lo local",
                    body: "Lanzamos por región - sé la primera en saber cuando tu área esté abierta.",
                },
            ],
        },
        form: {
            eyebrow: "Únete a la comunidad de propietarias",
            title1: "Te avisaremos cuando",
            titleAccent: "lancemos en tu área.",
            body:
                "Un formulario rápido. Sin spam - solo una nota personal cuando el directorio abra cerca de ti.",
            formTitle: "Únete a la Comunidad de Propietarias",
        },
    },
    providers: {
        seo: {
            title:
                "Para Proveedoras - Postúlate como Proveedora Fundadora",
            description:
                "¿Eres proveedora, contratista o profesional de oficios propiedad de mujeres? Postúlate para unirte a TrustHer Home como proveedora fundadora antes del lanzamiento.",
        },
        hero: {
            eyebrow: "Para Proveedoras Propiedad de Mujeres",
            title1: "Sé descubierta por las propietarias que",
            titleAccent: "quieren contratarte.",
            body:
                "TrustHer Home ayudará a contratistas, mujeres de oficios y proveedoras de servicios para el hogar propiedad de mujeres a ser encontradas por las propietarias que las buscan activamente. Postúlate ahora como proveedora fundadora.",
            cta: "Postúlate como Proveedora Fundadora",
        },
        why: {
            eyebrow: "Por qué unirte como proveedora fundadora",
            title1: "Diseñado para ayudar a tu negocio a",
            titleAccent: "destacar.",
            cards: [
                {
                    title: "Sé descubierta",
                    body: "Preséntate ante mujeres propietarias que buscan activamente proveedoras propiedad de mujeres.",
                },
                {
                    title: "Visibilidad fundadora",
                    body: "Las proveedoras fundadoras reciben ubicación destacada y reconocimiento en el lanzamiento.",
                },
                {
                    title: "Acceso anticipado",
                    body: "Sé la primera en reclamar tu perfil, categoría y área de servicio.",
                },
                {
                    title: "Moldea el directorio",
                    body: "Tu retroalimentación como proveedora fundadora ayudará a moldear la plataforma.",
                },
            ],
        },
        form: {
            eyebrow: "Postúlate ahora",
            title1: "Cuéntanos sobre tu",
            titleAccent: "negocio.",
            body:
                "Comparte algunos detalles a continuación. Las proveedoras fundadoras recibirán actualizaciones de lanzamiento y una invitación personal para reclamar su perfil cuando estemos en vivo.",
            nextTitle: "Qué sigue",
            nextSteps: [
                "1. Envía los detalles de tu postulación",
                "2. Revisaremos y confirmaremos por correo",
                "3. Tendrás acceso anticipado en el lanzamiento",
            ],
            formTitle: "Postúlate como Proveedora Fundadora",
        },
    },
    about: {
        seo: {
            title: "Sobre Nosotras",
            description:
                "Nuestra misión en TrustHer Home: conectar a mujeres propietarias con proveedoras, contratistas y profesionales de oficios propiedad de mujeres - construyendo confianza y visibilidad.",
        },
        hero: {
            eyebrow: "Sobre TrustHer Home",
            title1: "Creemos que las propietarias deberían sentirse",
            titleAccent: "seguras, respetadas y escuchadas",
            title2: "al contratar ayuda para sus hogares.",
        },
        story: {
            eyebrow: "Nuestra historia",
            lead:
                "TrustHer Home nació de una simple realización: las proveedoras de servicios para el hogar confiables y propiedad de mujeres son, con frecuencia, las más difíciles de encontrar - y las más buscadas.",
            p1:
                "Hablamos con propietarias que describieron la incomodidad de ser tratadas con condescendencia, de que les cobraran de más o de simplemente no ser escuchadas. Hablamos con mujeres en los oficios - talentosas carpinteras, electricistas, diseñadoras, paisajistas, pintoras - que dijeron que dependen casi por completo del boca a boca para hacer crecer sus negocios.",
            emphasis: "Ambos grupos merecen más.",
            p2:
                " Por eso construimos el directorio que por fin las pone frente a frente - con confianza, profesionalismo y visibilidad como base.",
        },
        values: {
            eyebrow: "Nuestros valores",
            title: "En qué creemos.",
            cards: [
                {
                    title: "Confianza primero",
                    body: "Cada proveedora listada será verificada. No tomaremos atajos en lo que importa.",
                },
                {
                    title: "Fácil de navegar",
                    body: "Busca por categoría, ubicación y necesidad. Sin desorden - solo decisiones seguras.",
                },
                {
                    title: "Celebramos a las mujeres",
                    body: "Desde contratistas hasta diseñadoras de interiores, celebramos a las mujeres que están dando forma al futuro de los servicios para el hogar.",
                },
            ],
        },
        ahead: {
            eyebrow: "Mirando adelante",
            title1: "Apenas estamos",
            titleAccent: "empezando.",
            p1:
                "Hoy estamos haciendo crecer nuestra comunidad de prelanzamiento de propietarias y proveedoras fundadoras. Mañana, TrustHer Home será un directorio completo con búsqueda - con perfiles de proveedoras, categorías de servicio, filtros por ubicación, reseñas y listados destacados.",
            p2:
                "Si nuestra misión resuena contigo, nos encantaría tenerte con nosotras desde el principio.",
            ctaHomeowner: "Únete a la Comunidad",
            ctaProvider: "Conviértete en Proveedora Fundadora",
        },
    },
    contact: {
        seo: {
            title: "Contáctanos",
            description:
                "Ponte en contacto con TrustHer Home. Escríbenos sobre el directorio, alianzas, prensa, o para unirte a la comunidad.",
        },
        hero: {
            eyebrow: "Ponte en contacto",
            title1: "Nos encantaría",
            titleAccent: "saber de ti.",
            body:
                "Lanzamos muy pronto. Únete a la lista, haz una pregunta o simplemente saluda - leemos cada mensaje.",
        },
        info: {
            emailLabel: "Correo",
            followLabel: "Sigue nuestro camino al lanzamiento",
            launchingTitle: "Lanzamos muy pronto.",
            launchingBody:
                "Únete a la lista y sé la primera en saber cuando el directorio abra en tu área.",
        },
        form: {
            nameLabel: "Tu nombre",
            namePlaceholder: "Jane Doe",
            emailLabel: "Correo electrónico",
            emailPlaceholder: "jane@email.com",
            messageLabel: "Mensaje",
            messagePlaceholder: "¿Qué tienes en mente?",
            submit: "Enviar mensaje",
            submitting: "Enviando...",
            successToast: "Mensaje enviado. Te responderemos pronto.",
            errorToast: "No se pudo enviar. Inténtalo de nuevo.",
        },
    },
    waitlist: {
        seo: {
            title: "Únete a la Comunidad",
            description:
                "Únete a la comunidad de TrustHer Home. Las propietarias reciben aviso en el lanzamiento; las proveedoras propiedad de mujeres pueden postularse como proveedoras fundadoras.",
        },
        hero: {
            eyebrow: "Participa",
            title1: "Lanzamos muy pronto.",
            titleAccent: "Sé la primera en saber.",
            body:
                "Elige tu camino abajo - ya sea que seas una propietaria lista para encontrar ayuda de confianza, o un negocio propiedad de mujeres listo para ser descubierto.",
        },
        tabs: {
            homeowner: "Soy Propietaria",
            provider: "Soy Proveedora",
        },
        homeowner: {
            title: "Únete a la Comunidad de Propietarias",
            body:
                "Recibe un aviso en el momento en que el directorio se lance en tu área. Sin spam - solo una nota personal en el lanzamiento.",
        },
        provider: {
            title: "Postulación de Proveedora Fundadora",
            body:
                "Las proveedoras fundadoras reciben acceso anticipado, visibilidad el día del lanzamiento y una línea directa con nuestro equipo mientras construimos el directorio.",
        },
    },
    notFound: {
        seo: {
            title: "Página no encontrada",
            description: "La página que buscas no existe.",
        },
        heading: "No podemos encontrar esa página.",
        body:
            "La página que buscas se ha movido o no existe. Volvamos a la página principal.",
        cta: "Llévame a inicio",
    },
};

export const translations = { en, es };

export function getTranslation(lang, key) {
    const dict = translations[lang] || translations.en;
    const parts = key.split(".");
    let cur = dict;
    for (const p of parts) {
        if (cur == null) return key;
        cur = cur[p];
    }
    if (cur == null) {
        // Fallback to English
        let fb = translations.en;
        for (const p of parts) {
            if (fb == null) return key;
            fb = fb[p];
        }
        return fb == null ? key : fb;
    }
    return cur;
}
